import React from "react";
import { NoteQuiz } from "@/components/games/NoteQuiz";
import { MarkSymbol } from "@/components/games/MarkSymbol";
import { EAR_GAMES } from "@/components/games/EarGames";
import { banksFor, bankTitle } from "@/data/markBanks";
import { useTerms } from "@/lib/termsStore";
import { getLessonContext } from "@/data/curriculum";

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

// Builds a game from one or more banks: half the questions show a symbol, half show a meaning.
const makeGame = (BANKS, keys, id, title) => {
  const pool = keys.flatMap((k) => (BANKS[k] || []).map((it) => ({ ...it, bank: k })));
  return {
    id, label: title, tip: "Some questions show a sign or word: pick what it means. Others give a meaning: pick the sign or word.",
    make: (prev) => {
      let it; do { it = pool[Math.floor(Math.random() * pool.length)]; } while (prev && it.id === prev.key && pool.length > 1);
      const same = pool.filter((x) => x.bank === it.bank && x.id !== it.id);
      const opts = shuffle([it, ...shuffle(same).slice(0, 3)]);
      const find = (cid) => pool.find((x) => x.id === cid);
      if (Math.random() < 0.5) {
        return { key: it.id, prompt: <div data-testid="mg-symbol"><MarkSymbol item={it} /></div>, choices: opts.map((o) => o.id),
          render: (cid) => find(cid).m, check: (c) => c === it.id, reveal: [it.id] };
      }
      return { key: it.id, prompt: <p className="text-base" data-testid="mg-meaning">Which one means <b className="text-primary">“{it.m}”</b>?</p>,
        choices: opts.map((o) => o.id), render: (cid) => <MarkSymbol item={find(cid)} size="sm" />, check: (c) => c === it.id, reveal: [it.id] };
    },
  };
};

// Per-lesson signs, terms and ear-training games for the Intermediate -> Advanced courses.
export const MarkGames = ({ lessonId }) => {
  const BANKS = useTerms();
  const keys = banksFor(lessonId)?.filter((k) => BANKS[k]?.length >= 2);
  if (!keys) return null;
  const ctx = getLessonContext(lessonId);
  const where = ctx ? `${ctx.instrument.label} · ${ctx.course.title} · Lesson ${ctx.number}` : "";
  const games = [...keys.map((k) => makeGame(BANKS, [k], k, bankTitle(k))), ...(keys.length > 1 ? [makeGame(BANKS, keys, `mix-${lessonId}`, "Mix of everything")] : []), ...EAR_GAMES];
  return <NoteQuiz title="Signs, terms & ear games" games={games} testId={`mark-games-${lessonId}`} unit="question" where={where} />;
};
